// src/components/discover/discover-page-client.tsx
"use client";

import React, { useEffect, useState, useCallback, useRef } from 'react';
import Link from 'next/link';
import CharacterCard from '@/components/discover/character-card';
import RandomCharacterCard from '@/components/discover/random-character-card';
import type { Character } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Users, Loader2, UserPlus, ShieldAlert } from 'lucide-react';
import { useI18n } from '@/context/i18n-context';
import { Alert, AlertDescription as UIDescription, AlertTitle as UITitle } from "@/components/ui/alert";
import { db } from '@/lib/firebase';
import { collection, query, orderBy, getDocs, doc, getDoc } from 'firebase/firestore'; 
import { cn } from '@/lib/utils';
import { fetchPaginatedCharacters } from '@/app/discover/actions';
import PublicCharacterGenerator from './public-character-generator';
import { useUIState } from '@/context/ui-state-context';


interface CuratedTagInfo {
  id: string;
  name: string;
  cacheUrl?: string | null;
}

const TRENDING_ITEM_ID = "__trending__";

export default function DiscoverPageClient() {
  const { t } = useI18n();
  const { setPageSpecificBackground } = useUIState();
  
  const [tagCaches, setTagCaches] = useState<Record<string, Character[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  
  const [displayedCharacters, setDisplayedCharacters] = useState<Character[]>([]);
  const [curatedTags, setCuratedTags] = useState<CuratedTagInfo[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [activeTagId, setActiveTagId] = useState<string>(TRENDING_ITEM_ID);

  // New state for infinite scroll
  const [paginatedChars, setPaginatedChars] = useState<Character[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const allDisplayedChars = [...displayedCharacters, ...paginatedChars];
  const allCharsRef = useRef(allDisplayedChars);
  allCharsRef.current = allDisplayedChars;


  const fetchTrendingCacheUrl = useCallback(async () => {
    if (!db) return null;
    try {
        const settingsDocRef = doc(db, 'pwaSettings', 'config');
        const docSnap = await getDoc(settingsDocRef);
        if (docSnap.exists() && docSnap.data().staticCacheUrl) {
            return docSnap.data().staticCacheUrl as string;
        }
    } catch (e) {
        console.error("Error fetching global settings for trending cache:", e);
    }
    return null;
  }, []);
  
  useEffect(() => {
    setPageSpecificBackground('/background.jpg');
    // Cleanup when component unmounts
    return () => {
      setPageSpecificBackground(null);
    };
  }, [setPageSpecificBackground]);

  useEffect(() => {
    const fetchTags = async () => {
        if (!db) return;
        setIsLoadingTags(true);
        try {
            const trendingCacheUrl = await fetchTrendingCacheUrl();
            const defaultTags: CuratedTagInfo[] = [{ id: TRENDING_ITEM_ID, name: t('trending'), cacheUrl: trendingCacheUrl }];
            
            const curatedTagsColRef = collection(db, "curatedTags");
            const q = query(curatedTagsColRef, orderBy("order", "asc"));
            const snapshot = await getDocs(q);
            const tagData = snapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name,
                cacheUrl: doc.data().staticCacheUrl || null,
            } as CuratedTagInfo));
            setCuratedTags([...defaultTags, ...tagData]);
        } catch (error) {
            console.error("Error fetching curated tags:", error);
            setCuratedTags([{ id: TRENDING_ITEM_ID, name: t('trending') }]);
        } finally {
            setIsLoadingTags(false);
        }
    };
    fetchTags();
  }, [t, db, fetchTrendingCacheUrl]);

  const fetchTagCache = useCallback(async (tagInfo: CuratedTagInfo) => {
    // Reset states for the new tag
    setIsLoading(true);
    setFetchError(null);
    setPaginatedChars([]);
    setDisplayedCharacters([]);
    setNextCursor(null);
    setHasMore(true);

    if (tagCaches[tagInfo.id]) {
      const cachedData = tagCaches[tagInfo.id];
      setDisplayedCharacters(cachedData);
      const lastCachedId = cachedData.length > 0 ? cachedData[cachedData.length - 1].id : null;
      setNextCursor(lastCachedId);
      setHasMore(!!lastCachedId);
      setIsLoading(false);
      return;
    }

    if (!tagInfo.cacheUrl) {
      const { characters, nextCursor: newCursor, error } = await fetchPaginatedCharacters({ tag: tagInfo.id === TRENDING_ITEM_ID ? undefined : tagInfo.name });
      if (error) {
          setFetchError(error);
          setDisplayedCharacters([]);
          setHasMore(false);
      } else {
          setDisplayedCharacters(characters);
          setNextCursor(newCursor);
          setHasMore(!!newCursor);
      }
      setIsLoading(false);
      return;
    }

    try {
      const proxyUrl = `/api/cors-proxy?url=${encodeURIComponent(tagInfo.cacheUrl)}`;
      const response = await fetch(proxyUrl, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Failed to load cache: ${response.statusText}`);
      
      const jsonData: Character[] = await response.json();
      setTagCaches(prev => ({ ...prev, [tagInfo.id]: jsonData }));
      setDisplayedCharacters(jsonData);

      const lastCachedId = jsonData.length > 0 ? jsonData[jsonData.length - 1].id : null;
      setNextCursor(lastCachedId);
      setHasMore(!!lastCachedId);
    } catch (e: any) {
      console.error(`Error fetching cache for tag '${tagInfo.name}':`, e);
      setFetchError(e.message || "An unknown error occurred while fetching the cache.");
      setTagCaches(prev => ({ ...prev, [tagInfo.id]: [] }));
      setDisplayedCharacters([]);
      setNextCursor(null);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [tagCaches]);
  
  const loadMoreCharacters = useCallback(async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return;
    
    setIsLoadingMore(true);

    const activeTagInfo = curatedTags.find(tag => tag.id === activeTagId);
    const tagToFilter = activeTagInfo?.id !== TRENDING_ITEM_ID ? activeTagInfo?.name : undefined;

    const result = await fetchPaginatedCharacters({ lastDocId: nextCursor, tag: tagToFilter });

    if (result.error) {
      setFetchError(result.error);
      setHasMore(false);
    } else {
      const existingIds = new Set(allCharsRef.current.map(c => c.id));
      const newUniqueChars = result.characters.filter(c => !existingIds.has(c.id));
      
      setPaginatedChars(prev => [...prev, ...newUniqueChars]);
      setNextCursor(result.nextCursor);
      if (!result.nextCursor) {
        setHasMore(false);
      }
    }
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, nextCursor, activeTagId, curatedTags]);

  // Infinite Scroll Observer Setup
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        loadMoreCharacters();
      }
    });

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, isLoadingMore, loadMoreCharacters]);

  useEffect(() => {
    const selectedTagInfo = curatedTags.find(tag => tag.id === activeTagId);
    if (selectedTagInfo) {
      fetchTagCache(selectedTagInfo);
    }
  }, [activeTagId, curatedTags, fetchTagCache]);

  const renderSkeletons = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
      {Array.from({ length: 12 }).map((_, index) => (
        <div key={`skeleton-${index}`} className="bg-[hsl(var(--glass-character-card-bg))] backdrop-blur-md border-transparent animate-pulse overflow-hidden h-full flex flex-col group rounded-2xl shadow-lg">
          <div className="relative w-full aspect-[2/3] bg-white/5 rounded-2xl"></div>
        </div>
      ))}
    </div>
  );
  
  const renderErrorState = (errorMsg: string | null) => (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-var(--header-height,4rem)-15rem)] text-center p-4 md:p-8">
      <ShieldAlert className="h-16 w-16 text-destructive mb-4" />
      <Alert variant="destructive" className="max-w-md bg-destructive/20 border-destructive text-destructive-foreground">
        <UITitle>{t('error')}</UITitle>
        <UIDescription>{errorMsg || t('errorFetchingCharacters')}</UIDescription>
      </Alert>
    </div>
  );

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center text-center py-12 border-2 border-dashed border-transparent rounded-lg bg-[hsl(var(--glass-content-panel-bg))] backdrop-blur-md shadow-xl">
      <Users className="h-20 w-20 text-[hsl(var(--glass-text-muted))] mb-6" />
      <h2 className="text-2xl font-semibold text-[hsl(var(--glass-text))] mb-2">
        {t('noCharactersFound')}
      </h2>
      <p className="text-[hsl(var(--glass-text-muted))] mb-8 max-w-md">
        {t('noCharactersDesc')}
      </p>
      <Link href="/create-character" passHref>
          <Button
            size="lg"
            className="bg-white/10 hover:bg-white/20 border border-white/30 text-foreground backdrop-blur-sm rounded-lg shadow-lg"
          >
            <UserPlus className="mr-2 h-4 w-4" /> {t('createCharacter')}
          </Button>
        </Link>
    </div>
  );

  return (
    <section>
        <div className="w-full -mb-[50px]">
            <PublicCharacterGenerator />
        </div>

       <div className="sticky top-[76px] z-10 bg-[hsl(var(--glass-content-panel-bg))] backdrop-blur-md rounded-2xl shadow-lg px-4 md:px-6 py-3">
        <div className="pb-1">
            <div className="flex flex-wrap gap-2">
                {isLoadingTags ? (
                Array.from({ length: 5 }).map((_, i) => (
                    <div key={`tag-skeleton-${i}`} className="h-8 w-20 rounded-full bg-white/10 animate-pulse" />
                ))
                ) : (
                curatedTags.map((tag) => (
                    <Button
                    key={tag.id}
                    variant={activeTagId === tag.id ? 'default' : 'secondary'}
                    onClick={() => setActiveTagId(tag.id)}
                    className={cn(
                        'rounded-full h-8 px-4 text-xs shrink-0',
                        activeTagId === tag.id
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg'
                        : 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white'
                    )}
                    >
                    {tag.name}
                    </Button>
                ))
                )}
            </div>
        </div>
      </div>

      <div className="pt-6">
        {fetchError ? renderErrorState(fetchError)
         : isLoading ? renderSkeletons()
         : (allDisplayedChars.length > 0) ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {activeTagId === TRENDING_ITEM_ID && displayedCharacters.length > 0 && <RandomCharacterCard characters={displayedCharacters} />}
              {allDisplayedChars.map((character) => (
                character && <CharacterCard key={character.id} character={character} />
              ))}
            </div>
            <div ref={loadMoreRef} className="h-10 flex justify-center items-center">
              {isLoadingMore && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
              {!hasMore && allDisplayedChars.length > 0 && (
                <p className="text-sm text-muted-foreground">{t('noMoreCharacters')}</p>
              )}
            </div>
          </>
        ) : (
          renderEmptyState()
        )}
      </div>
    </section>
  );
}
